export const TASK_STATE_VALUES = ['Extracted', 'Confirmed', 'Completed'] as const;
export type TaskState = typeof TASK_STATE_VALUES[number];

/**
 * Deadline state configuration for UI rendering
 */
export const STATE_CONFIG: Record<TaskState, { bg: string; text: string; label: string; value: string }> = {
  Extracted: { bg: 'bg-yellow-50', text: 'text-yellow-800', label: 'Needs Confirmation', value: 'Extracted' },
  Confirmed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Confirmed', value: 'Confirmed' },
  Completed: { bg: 'bg-green-50', text: 'text-green-800', label: 'Completed', value: 'Completed' },
};

export const DEFAULT_TASK_STATE: TaskState = 'Confirmed';

export function isTaskState(value: unknown): value is TaskState {
  return typeof value === 'string' && (TASK_STATE_VALUES as readonly string[]).includes(value);
}

export function normalizeTaskState(value: unknown): TaskState {
  return isTaskState(value) ? value : DEFAULT_TASK_STATE;
}

export function getTaskStateConfig(value: unknown) {
  const state = normalizeTaskState(value);
  return STATE_CONFIG[state];
}

export interface StatusTransition {
  label: string;
  action: () => void;
}

/** The state-transition menu items available for a deadline, in the order shown
 *  by both the inline status control and the detail panel. */
export function getStatusTransitions({
  state,
  isExtracted,
  onMoveToNeedsConfirmation,
  onConfirm,
  onComplete,
}: {
  state?: string | null;
  /** Whether the row was auto-extracted (i.e. has no `created_by`). */
  isExtracted: boolean;
  onMoveToNeedsConfirmation: () => void;
  onConfirm: () => void;
  onComplete: () => void;
}): StatusTransition[] {
  const normalized = normalizeTaskState(state);
  const transitions: StatusTransition[] = [];
  if (normalized !== 'Extracted' && isExtracted) {
    transitions.push({ label: 'Move to Needs Confirmation', action: onMoveToNeedsConfirmation });
  }
  if (normalized !== 'Confirmed') {
    transitions.push({ label: 'Move to Confirmed', action: onConfirm });
  }
  if (normalized !== 'Completed') {
    transitions.push({ label: 'Mark as Completed', action: onComplete });
  }
  return transitions;
}

/**
 * Status filter options
 */
export const STATUS_OPTIONS = [
  { label: 'Needs Confirmation', value: 'Extracted' },
  { label: 'Confirmed', value: 'Confirmed' },
  { label: 'Completed', value: 'Completed' },
  { label: 'Overdue', value: 'Overdue' },
];

/**
 * UI constants
 */
export const UI_CONSTANTS = {
  HIGHLIGHT_COLOR: '#FFEA00',
  HIGHLIGHT_TEXT_COLOR: 'black',
} as const;
